import { useTranslation } from "react-i18next";
import React, { Fragment, useEffect, useState } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import Loading from "../../../components/Loading";
import Button from "../../../components/Button";
import StatusBadge from "../../../components/StatusBadge";
import { Dialog, Transition } from "@headlessui/react";
import { useQuery } from "react-query";
import { apollo } from "../../../utils/apollo";
import { toast } from "react-toastify";
import {
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import {
  VALIDATION_QUOTES,
  UPDATE_VALIDATION_QUOTE_STATUS,
} from "../../../utils/graphql";
function ValidationList() {
  const { t } = useTranslation();
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingQuote, setPendingQuote] = useState(null);
  const normalizeAttachments = (att) => {
    if (!att) return [];
    if (typeof att === "string") {
      try {
        att = JSON.parse(att);
      } catch (e) {
        return [];
      }
    }
    if (Array.isArray(att))
      return att.map((u, i) => ({
        key: t("validation.attachment_with_count", { count: i + 1 }),
        url: u,
      }));
    if (typeof att === "object")
      return Object.entries(att).map(([k, v]) => ({
        key: k,
        url: v,
      }));
    return [];
  };
  const AttachmentItem = ({ label, url }) => (
    <div className="border rounded p-2">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] font-medium truncate" title={label}>
          {label}
        </div>
        <a
          className="text-primary-600 text-[11px] hover:underline shrink-0 inline-flex items-center gap-1"
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          <ArrowTopRightOnSquareIcon className="h-3 w-3" />
          {t("Open")}
        </a>
      </div>
      {url ? (
        <div className="relative w-full">
          <iframe
            src={url}
            title={label}
            className="w-full h-64 border rounded"
          />
        </div>
      ) : null}
    </div>
  );

  // Small helpers
  const formatCurrency = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return value || "-";
    return `AED ${num.toLocaleString(undefined, {
      maximumFractionDigits: 2,
    })}`;
  };
  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toLocaleString();
  };
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.info("Copied to clipboard");
    } catch (e) {}
  };
  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = useQuery(
    "validation_quotes",
    async () => {
      const { data } = await apollo.query({
        query: VALIDATION_QUOTES,
      });
      return data?.validationQuotes || [];
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      // Auto-refresh every 30s unless the quote modal is open
      refetchInterval: modalOpen ? false : 30000,
    },
  );
  const openModal = (quote) => {
    setSelectedQuote(quote);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setSelectedQuote(null);
  };

  // Confirmation flow
  const requestStatusUpdate = (quote, status) => {
    setPendingQuote(quote);
    setPendingAction(status);
    setConfirmOpen(true);
  };
  const cancelConfirm = () => {
    if (busy) return;
    setConfirmOpen(false);
    setPendingAction(null);
    setPendingQuote(null);
  };
  const confirmAndUpdate = async () => {
    if (!pendingQuote || !pendingAction) return;
    await handleUpdateStatus(pendingQuote, pendingAction);
    setConfirmOpen(false);
    setPendingAction(null);
    setPendingQuote(null);
  };
  const handleUpdateStatus = async (quote, status) => {
    try {
      setBusy(true);
      const { data } = await apollo.mutate({
        mutation: UPDATE_VALIDATION_QUOTE_STATUS,
        variables: {
          input: {
            hostname: quote.hostname,
            uid: quote.uid,
            status: status,
          },
        },
      });
      const res = data?.updateValidationQuoteStatus;
      if (res?.success) {
        toast.success(res?.message || `Quote ${status}`);
        await refetch();
        closeModal();
      } else {
        toast.error(res?.message || "Failed to update quote");
      }
    } catch (e) {
      toast.error("Failed to update quote");
    } finally {
      setBusy(false);
    }
  };

  // Keyboard shortcuts inside the details modal
  useEffect(() => {
    if (!modalOpen || !selectedQuote) return;
    const onKeyDown = (e) => {
      if (busy) return;
      const key = (e.key || "").toLowerCase();
      if (key === "a") {
        e.preventDefault();
        requestStatusUpdate(selectedQuote, "approved");
      } else if (key === "d") {
        e.preventDefault();
        requestStatusUpdate(selectedQuote, "disabled");
      } else if (e.key === "Escape") {
        e.preventDefault();
        closeModal();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalOpen, selectedQuote, busy]);
  return (
    <DashboardLayout>
      <PageHeader
        title={t("validation.page_title")}
        description={t("validation.page_description")}
      />

      {/* Toolbar */}
      <div className="mt-2 flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {t("Auto-refresh")}
          {modalOpen ? "(paused while viewing details)" : "every 30s"}
          {t(".") + " " + t("Last updated")}:
          {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : "-"}
        </div>
        <div>
          <Button
            secondary
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1"
          >
            <ArrowPathIcon
              className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
            />
            {t("Refresh")}
          </Button>
        </div>
      </div>

      {isLoading && <Loading />}
      {!isLoading && (!data || data.length === 0) && (
        <div className="p-10 text-center text-gray-600">
          <div className="text-sm">{t("No pending quotes right now.")}</div>
          <div className="mt-3 flex justify-center">
            <Button
              secondary
              onClick={() => refetch()}
              className="inline-flex items-center gap-1"
            >
              <ArrowPathIcon className="h-4 w-4" />
              {t("Try Refresh")}
            </Button>
          </div>
        </div>
      )}
      {!isLoading && data && data.length > 0 && (
        <div className="space-y-8 mt-4">
          {data.map((group, gi) => (
            <div key={gi} className="bg-white border rounded-lg shadow">
              <div className="px-4 py-3 border-b font-semibold">
                {group.company}
              </div>
              <div className="divide-y">
                {group.quotes?.map((q, qi) => (
                  <div
                    key={qi}
                    className="p-4 grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
                  >
                    <div className="col-span-6">
                      <div className="text-sm text-gray-500">
                        {t("Reference")}
                      </div>
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        <span>{q.reference}</span>
                        <span className="text-gray-400">({q.uid})</span>
                        <button
                          className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1"
                          onClick={() =>
                            copyToClipboard(`${q.reference} (${q.uid})`)
                          }
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                          {t("Copy")}
                        </button>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {t("Product")}:
                        {q.product}
                        {"\u2022 " + t("Premium")}:
                        {formatCurrency(q.total_premium)}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                        <span>{t("State")}:</span>
                        <StatusBadge status={q.state || "-"} />
                      </div>
                    </div>
                    <div className="col-span-3">
                      <div className="text-sm font-medium">{t("Sponsor")}</div>
                      {q.sponsor && (
                        <div className="text-xs text-gray-600 mt-1">
                          <div>{q.sponsor.fullName}</div>
                          <div>
                            {t("EID")}:
                            {q.sponsor.emiratesIDNo}
                          </div>
                          <div>
                            {t("Trade License")}:
                            {q.sponsor.tradeLicenseNumber}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="col-span-3">
                      <div className="flex items-center gap-2 justify-end">
                        <Button onClick={() => openModal(q)}>
                          {t("View")}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for viewing details */}
      <Transition.Root show={modalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={setModalOpen}
          key={selectedQuote?.uid ?? "default-modal"}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/60" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="transition ease-in-out duration-200 transform"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-4xl transform overflow-hidden rounded-lg bg-white text-start shadow-xl transition-all">
                  <div className="px-4 py-3 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                    <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
                      {t("Quote Details")}
                    </Dialog.Title>
                    <button
                      onClick={closeModal}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      {t("Close")}
                    </button>
                  </div>

                  <div className="p-4 sm:p-6 space-y-6">
                    {!selectedQuote && (
                      <div className="text-gray-600">
                        {t("No quote selected.")}
                      </div>
                    )}
                    {selectedQuote && (
                      <div className="space-y-6">
                        <div className="bg-white">
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            <div className="md:col-span-8">
                              <div className="text-sm text-gray-500">
                                {t("Reference")}
                              </div>
                              <div className="font-medium flex items-center gap-2 flex-wrap">
                                <span>{selectedQuote.reference}</span>
                                <span className="text-gray-400">
                                  ({selectedQuote.uid})
                                </span>
                                <button
                                  className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1"
                                  onClick={() =>
                                    copyToClipboard(
                                      `${selectedQuote.reference} (${selectedQuote.uid})`,
                                    )
                                  }
                                >
                                  <DocumentDuplicateIcon className="h-4 w-4" />
                                  {t("Copy")}
                                </button>
                              </div>
                              <div className="mt-1 text-xs text-gray-500">
                                {t("Product")}:
                                {selectedQuote.product}
                                {"\u2022 " + t("Premium")}:
                                {formatCurrency(selectedQuote.total_premium)}
                              </div>
                              <div className="mt-1 text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                                <span>{t("State")}:</span>
                                <StatusBadge
                                  status={selectedQuote.state || "-"}
                                />
                              </div>
                              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                                <div>
                                  {t("Policy No")}:
                                  {selectedQuote.policy_number || "-"}
                                </div>
                                <div>
                                  {t("Sponsor")}:
                                  {selectedQuote.sponsor_name ||
                                    selectedQuote.sponsor?.fullName ||
                                    "-"}
                                </div>
                                <div>
                                  {t("Created")}:
                                  {formatDate(selectedQuote.created_at)}
                                </div>
                                <div>
                                  {t("Paid At")}:
                                  {formatDate(selectedQuote.paid_at)}
                                </div>
                              </div>
                              <div className="mt-2 text-xs">
                                <a
                                  className="text-primary-600 hover:underline inline-flex items-center gap-1"
                                  href={selectedQuote.quote_link}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {t("Open Quote in click2secure")}
                                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white">
                          <div className="font-semibold mb-2">
                            {t("Sponsor")}
                          </div>
                          {selectedQuote.sponsor ? (
                            <div className="text-sm text-gray-700">
                              <div>{selectedQuote.sponsor.fullName}</div>
                              <div className="text-gray-500">
                                {t("EID")}:
                                {selectedQuote.sponsor.emiratesIDNo}
                              </div>
                              <div className="text-gray-500">
                                {t("Trade License")}:
                                {selectedQuote.sponsor.tradeLicenseNumber}
                              </div>
                              {normalizeAttachments(
                                selectedQuote.sponsor.attachments,
                              ).length > 0 && (
                                <div className="mt-2">
                                  <div className="text-xs font-semibold uppercase text-gray-500">
                                    {t("Attachments")}
                                  </div>
                                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-4 gap-4">
                                    {normalizeAttachments(
                                      selectedQuote.sponsor.attachments,
                                    ).map(({ key, url }) => (
                                      <AttachmentItem
                                        key={key}
                                        label={key}
                                        url={url}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">
                              {t("No sponsor info")}
                            </div>
                          )}
                        </div>

                        <div className="bg-white">
                          <div className="font-semibold mb-2">
                            {t("Members")}
                          </div>
                          {Array.isArray(selectedQuote.members) &&
                          selectedQuote.members.length > 0 ? (
                            <div className="grid grid-cols-1 gap-4">
                              {selectedQuote.members.map((m, idx) => (
                                <div key={idx} className="border rounded p-3">
                                  <div className="font-medium">
                                    {m.fullNameEn || "Member"}
                                  </div>
                                  <div className="text-xs text-gray-500 space-x-2">
                                    {t("Gender")}:
                                    {m.gender || "-"}
                                  </div>
                                  <div className="text-xs text-gray-500 space-x-2">
                                    {t("DOB")}:
                                    {m.dateOfBirth || "-"}
                                  </div>
                                  <div className="text-xs text-gray-500 space-x-2">
                                    {t("EID")}:
                                    {m.emiratesIDNo || "-"}
                                  </div>
                                  <div className="text-xs text-gray-500 space-x-2">
                                    {t("Unified No")}:
                                    {m.unifiedNo || "-"}
                                  </div>
                                  <div className="text-xs text-gray-500 space-x-2">
                                    {t("Visa")}:
                                    {m.visaFileNo || "-"}
                                  </div>
                                  <div className="text-xs text-gray-500 space-x-2">
                                    {t("City")}:
                                    {m.residenceVisaCity || "-"}
                                  </div>
                                  <div className="text-xs text-gray-500 space-x-2">
                                    {t("Nationality")}:
                                    {m.nationality || "-"}
                                  </div>
                                  {normalizeAttachments(m.attachments).length >
                                    0 && (
                                    <div className="mt-2">
                                      <div className="text-xs font-semibold uppercase text-gray-500">
                                        {t("Attachments")}
                                      </div>
                                      <div className="mt-1 grid grid-cols-1 sm:grid-cols-4 gap-4">
                                        {normalizeAttachments(
                                          m.attachments,
                                        ).map(({ key, url }) => (
                                          <AttachmentItem
                                            key={key}
                                            label={key}
                                            url={url}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">
                              {t("No members")}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-3 border-t bg-white sticky bottom-0 z-10 flex items-center justify-end gap-2">
                    <Button
                      onClick={() =>
                        requestStatusUpdate(selectedQuote, "approved")
                      }
                      disabled={busy}
                      className="inline-flex items-center gap-1"
                    >
                      <CheckIcon className="h-4 w-4" />
                      {t("Approve")}
                    </Button>
                    <Button
                      secondary
                      onClick={() =>
                        requestStatusUpdate(selectedQuote, "disabled")
                      }
                      disabled={busy}
                      className="inline-flex items-center gap-1 from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500"
                    >
                      <XMarkIcon className="h-4 w-4" />
                      {t("Disable")}
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      {/* Confirmation modal */}
      <Transition.Root show={confirmOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[70]"
          onClose={(value) => {
            if (!busy) setConfirmOpen(value);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/60" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="transition ease-in-out duration-200 transform"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white text-start shadow-xl transition-all">
                  <div className="px-4 py-3 border-b">
                    <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
                      {pendingAction === "disabled"
                        ? t("validation.disable_quote")
                        : t("validation.approve_quote")}
                    </Dialog.Title>
                  </div>
                  <div className="p-4 sm:p-6">
                    <p className="text-sm text-gray-700">
                      {t("validation.are_you_sure_to")}{" "}
                      {pendingAction === "disabled"
                        ? t("validation.disable_action")
                        : t("validation.approve_action")}{" "}
                      {t("validation.this_quote")}
                      {pendingQuote
                        ? ` ${pendingQuote.reference} (${pendingQuote.uid})`
                        : ""}
                      ?
                    </p>
                    <p className="mt-2 text-xs text-gray-500">
                      {t("validation.update_quote_status_hint")}
                    </p>
                    <div className="mt-6 flex items-center justify-end gap-2">
                      <Button secondary onClick={cancelConfirm} disabled={busy}>
                        {t("Cancel")}
                      </Button>
                      <Button
                        onClick={confirmAndUpdate}
                        disabled={busy}
                        className={`${pendingAction === "disabled" ? "from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500" : ""}`}
                      >
                        {busy
                          ? t("common.processing")
                          : pendingAction === "disabled"
                            ? t("validation.disable")
                            : t("validation.approve")}
                      </Button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </DashboardLayout>
  );
}
export default ValidationList;
